
import * as d3 from 'd3';
import { DateTime, Duration, Interval } from 'luxon';
import { TimelineSegmentSpec } from '../widgets/Constants';
import { SelectionInterval } from './SelectionInterval';

// These cutoffs are as a function of viewport size
//const _mtd_cutoffs = [    'PT12H', 'PT36H', 'PT108H',  'P10D',  'P45D', 'P3Y', 'P35Y', 'P250Y'   ];
const _mtd_cutoffs = [    992800., 330933.3, 99280., 49640., 11031.1, 453.3, 38.9,   5.4,     0.54 ].reverse(); // Pixels per year
const _mtd_values = ['PT30M',  'PT1H',  'PT3H', 'PT12H', 'P1D',  'P1M', 'P1Y', 'P10Y', 'P100Y', 'P500Y' ].reverse();
const mtd_cutoffs = _mtd_cutoffs;
const mtd_values = _mtd_values.map(s => Duration.fromISO(s));

//const _dintv_cutoffs = [   'P3D',   'P10D',  'P1Y6M',  'P250Y'   ];
const _dintv_cutoffs = [   165466.7, 49640., 906.7,    68.,     5.4,  0.54].reverse(); // Pixels per year (assuming a width of 1360 px)
const _dintv_values = ['PT1H',  'PT3H',   'P1D',  'P1M',  'P1Y', 'P10Y', 'P100Y'].reverse();
const dintv_cutoffs = _dintv_cutoffs;
const dintv_values = _dintv_values.map(s => Duration.fromISO(s));

// These cutoffs are as a function of mtd (major tick duration) size
const _blf_cutoffs = [    'PT1H', 'PT24H', 'P7D', 'P1Y'   ];
const _blf_values = ['%H%MZ',  '%HZ',   '%d',  '%m',  null];
const blf_cutoffs = _blf_cutoffs.map(s => Duration.fromISO(s));
const blf_values = _blf_values.map(s => s === null ? () => '' : d3.utcFormat(s));

const _tlf_cutoffs = [        'P1D',    'P7D'    ];
const _tlf_values = ['%Y-%m-%d',  '%Y-%m',   '%Y'];
const tlf_cutoffs = _tlf_cutoffs.map(s => Duration.fromISO(s));
const tlf_values = _tlf_values.map(s => s === null ? () => '' : d3.utcFormat(s));
const tlf_checks = [(jsdt: Date) => jsdt.getUTCHours() == 0 && jsdt.getUTCMinutes() == 0, 
                    (jsdt: Date) => jsdt.getUTCDate() == 1,
                    (jsdt: Date) => jsdt.getUTCMonth() == 0 && jsdt.getUTCDate() == 1]

const orient_cutoff = Duration.fromISO('P1Y');

const stepwise = <T1, T2>(x_val: T1, x_cutoffs: T1[], y_values: T2[]) : T2 => {
    let y_val: (null | T2) = null;

    x_cutoffs.some((xc, ic) => {
        if (x_val < xc) {
            y_val = y_values[ic];
            return true;
        }
    });

    if (y_val === null) {
        y_val = y_values[y_values.length - 1];
    }

    return y_val;
}

type DurationKeys = 'years' | 'months' | 'days' | 'hours' | 'minutes';
const luxon_d3_conversion: Record<DurationKeys, d3.CountableTimeInterval> = 
        {'years': d3.utcYear, 'months': d3.utcMonth, 'days': d3.utcDay, 'hours': d3.utcHour, 'minutes': d3.utcMinute};

interface TimelineSegment {
    domain: Interval;
    range: number[];
    mtd: Duration;
    data_dintv: Duration;
    bl_format: (jsdt: Date) => string;
    tl_format: (jsdt: Date) => string;
    tl_check: (jsdt: Date) => boolean;
    draw_orient: boolean;
    slope: number;
    visible: boolean;
    scale: d3.ScaleTime<number, number>;
}

class MultiscaleTimeAxis {
    intv: SelectionInterval;
    xlb: number;
    xub: number;
    t_limits: Interval;
    t_buffer: number;
    nonlinear_time: TimelineSegmentSpec[];
    min_scale?: Duration;

    t_bnds_intv: Interval;
    seg_start: TimelineSegmentSpec;
    seg_end: TimelineSegmentSpec;

    scale: TimelineSegment[];

    constructor(intv: SelectionInterval, xbnds: [number, number], t_limits: Interval, t_buffer: number, nonlinear_time: TimelineSegmentSpec[], min_scale?: Duration) {
        this.intv = intv;
        [this.xlb, this.xub] = xbnds;
        this.t_limits = t_limits;
        this.t_buffer = t_buffer;
        this.nonlinear_time = nonlinear_time;
        this.min_scale = min_scale;

        const width = this.xub - this.xlb;

        this.t_bnds_intv = this.intv.getIntervals()[0];
        const intv_dur = Duration.fromMillis(Math.max(1, 700 / width) * this.t_bnds_intv.toDuration().as('milliseconds'));

        let scale_intv : Interval = this.t_bnds_intv

        const timeline_sizes = this.nonlinear_time.map(seg => seg['scale'] * seg['domain'].length('days'));
        const range_start_idx = this.nonlinear_time.map(seg => seg['domain'].contains(scale_intv.start)).indexOf(true);
        const range_end_idx = this.nonlinear_time.map(seg => seg['domain'].contains(scale_intv.end)).indexOf(true);

        this.seg_start = this.nonlinear_time[range_start_idx];
        this.seg_end = this.nonlinear_time[range_end_idx];

        const range_start_scale = timeline_sizes.slice(0, range_start_idx).reduce((a, b) => a + b, 0) 
            + this.seg_start['scale'] * (scale_intv.start.diff(this.seg_start['domain'].start).as('days'));
        const range_end_scale = timeline_sizes.slice(0, range_end_idx).reduce((a, b) => a + b, 0) 
            + this.seg_end['scale'] * (scale_intv.end.diff(this.seg_end['domain'].start).as('days'));
        const time_scale = width / (range_end_scale - range_start_scale);

        this.scale = this.nonlinear_time.map((seg, iseg) => {
            const seg_scale_start: number = timeline_sizes.slice(0, iseg).reduce((a, b) => a + b, 0);
            const seg_scale_end = seg_scale_start + seg['scale'] * seg['domain'].length('days');
            let range_start, range_end;

            range_start = this.xlb + (seg_scale_start - range_start_scale) * time_scale;
            range_end = this.xlb + (seg_scale_end - range_start_scale) * time_scale;

            const slope = seg['domain'].length('seconds') / (range_end - range_start);

            let domain = scale_intv.intersection(seg['domain']);
            let range = [range_start, range_end];
            let range_full = [...range];

            let visible: boolean;
            if (domain === null) {
                domain = seg['domain'];
                visible = false;
            }
            else {
                range = range.map(x => Math.max(this.xlb, Math.min(this.xub, x)));
                visible = true;
            }

            const intv_dur = (range[1] - range[0]) / domain.toDuration().as('years');

            const mtd = stepwise(intv_dur, mtd_cutoffs, mtd_values);
            const data_dintv = stepwise(intv_dur, dintv_cutoffs, dintv_values);
            const bl_format = stepwise(mtd, blf_cutoffs, blf_values);
            const tl_format = stepwise(mtd, tlf_cutoffs, tlf_values);
            const tl_check = stepwise(mtd, tlf_cutoffs, tlf_checks);
            const draw_orient = mtd < orient_cutoff;
            
            return {'domain': seg['domain'], 'range': range_full, 'mtd': mtd, 'data_dintv': data_dintv,
                'bl_format': bl_format, 'tl_format': tl_format, 'tl_check': tl_check, 'draw_orient': draw_orient,
                'slope': slope, 'visible': visible,
                'scale': d3.scaleUtc()
                .domain([domain.start.toJSDate(), domain.end.toJSDate()])
                .range(range)};
        });
    }

    withMinScale(min_scale: Duration) : MultiscaleTimeAxis {
        return new MultiscaleTimeAxis(this.intv, [this.xlb, this.xub], this.t_limits, this.t_buffer, this.nonlinear_time, min_scale);
    }

    withInterval(intv: SelectionInterval) : MultiscaleTimeAxis {
        let taxis: MultiscaleTimeAxis = this;

        if (intv.start() < this.t_limits.start || intv.end() > this.t_limits.end) {
            // Iterate getting the padded bounds to converge to the proper padding
            for (let i = 0; i < 3; i++) {
                const bounds = taxis.getPaddedBounds();
                const intv_intersect = intv.getIntervals()[0].intersection(bounds);
        
                if (intv_intersect === null) {
                    throw "Pad interval is outside the data bounds";
                }
        
                taxis = new MultiscaleTimeAxis(new SelectionInterval(intv_intersect), 
                                               [this.xlb, this.xub], this.t_limits, this.t_buffer, this.nonlinear_time, this.min_scale);
            }
        }
        else {
            taxis = new MultiscaleTimeAxis(intv, [this.xlb, this.xub], this.t_limits, this.t_buffer, this.nonlinear_time, this.min_scale);
        }
        return taxis;
    }

    withXBounds(xbnds: [number, number]) : MultiscaleTimeAxis {
        return new MultiscaleTimeAxis(this.intv, xbnds, this.t_limits, this.t_buffer, this.nonlinear_time, this.min_scale);
    }

    getBarSubintervals(cday_offset: Duration, start_midyear?: boolean) : Interval[] {
        const zoom_limit_intv = this.getPaddedBounds();
        const scale_intv_intsct = zoom_limit_intv.intersection(this.t_bnds_intv);
        const scale_intv = scale_intv_intsct === null ? zoom_limit_intv : scale_intv_intsct;
        start_midyear = start_midyear === undefined ? false : start_midyear;

        return this.scale.map(sc => {
            let domain = scale_intv.intersection(sc['domain']);

            if (domain === null) {
                return [];
            }
            domain = Interval.fromDateTimes(domain.start, domain.end.minus({'millisecond': 1}));

            let data_dintv = sc['data_dintv'];
            if (this.min_scale !== undefined) {
                data_dintv = data_dintv > this.min_scale ? data_dintv : this.min_scale;
            }

            return (new SelectionInterval(domain)).getSubintervals(data_dintv, cday_offset, start_midyear);
        }).flat();
    }

    map(dt: DateTime) : number{
        const scale = this.scale.filter(sc => sc['domain'].contains(dt))[0];
        return Math.max(this.xlb - 10, Math.min(this.xub + 10, scale['scale'](dt.toJSDate())));
    }

    mapInverse(x: number) : DateTime {
        let scale = this.scale.filter(sc => sc['range'][0] <= x && x <= sc['range'][1])[0];
        if (scale === undefined) {
            if (x < this.scale[0]['range'][0]) {
                scale = this.scale[0];
            }
            else {
                scale = this.scale[this.scale.length - 1];
            }
        }
        return DateTime.fromJSDate(scale['scale'].invert(x), {'zone': 'UTC'});
    }

    getScales() {
        return this.scale.filter(sc => sc['range'][0] <= this.xub && sc['range'][1] >= this.xlb).map(sc => sc['scale']);
    }

    getTicks(d3scale: d3.ScaleTime<number, number>) : d3.TimeInterval {
        const scale = this.scale.filter(sc => sc['scale'] === d3scale)[0];

        let d3_ticks: (d3.TimeInterval | null) = null;
        for (let comp in luxon_d3_conversion) {
            const comp_key = comp as DurationKeys;
            if (scale['mtd'][comp_key] > 0) {
                d3_ticks = luxon_d3_conversion[comp_key].every(scale['mtd'][comp_key]);
                break
            }
        }
        if (d3_ticks === null) {
            throw "Couldn't find a relevant tick duration in MultiscaleTimeAxis.getTicks()";
        }

        return d3_ticks;
    }

    formatTick(jsdt: Date) : string {
        const dt = DateTime.fromJSDate(jsdt);
        const scale = this.scale.filter(sc => sc['domain'].contains(dt))[0];

        let str = scale['bl_format'](jsdt);

        if (scale['tl_check'](jsdt)) {
            if (str != "") {
                str += " ";
            }
            str += scale['tl_format'](jsdt);
        }
        return str;
    }

    getPaddedBounds() : Interval {
        const timeline_slope = this.scale.map(seg => isNaN(seg['slope']) ? 0 : seg['slope']);

        const buffer_dur_lower = this.t_buffer * timeline_slope[0]
        const buffer_dur_upper = this.t_buffer * timeline_slope[timeline_slope.length - 1];
    
        const earliest_dt = this.t_limits.start.minus({'seconds': buffer_dur_lower});
        const latest_dt = this.t_limits.end.plus({'seconds': buffer_dur_upper});
    
        return Interval.fromDateTimes(earliest_dt, latest_dt);
    }

    getOrientLabel() : (string | null) {
        const scale = this.scale.filter(s => s['visible'] == true)[0];
        if (scale['draw_orient']) {
            return scale['tl_format'](this.t_bnds_intv.start.toJSDate());
        }
        return null;
    }
}

export default MultiscaleTimeAxis;